import type { IApiRequestPageable } from '@/shared/types/api'
import type { IActiveStep, IDonationType } from '@/entities/donor/model'

export const EVisitStatus = {
  PRE_REGISTERED: 'PRE_REGISTERED',
  APPROVED: 'APPROVED',
  TO_THERAPIST: 'TO_THERAPIST',
  THERAPIST_EXAMINED: 'THERAPIST_EXAMINED',
  SCREENING: 'SCREENING',
  DONATION: 'DONATION',
  REJECTED: 'REJECTED',
  PENDING: 'PENDING',
}
export type TVisitStatus = (typeof EVisitStatus)[keyof typeof EVisitStatus]

export interface IVisit {
  uid: string
  fio_initials: string
  birth_date: string
  donor_type: string
  donation_type: IDonationType
  donation_type_name: string
  /** The furthest stage, as localized text — «НЕТ АКТИВНЫХ СТАТУСОВ» when finished. */
  direction_badge: string
  /** Every current business status, furthest first (BS-285). */
  active_steps?: IActiveStep[]
  visit_date: string
  status: TVisitStatus
  visit_id: string
}
export interface IVisitAdmitted extends IVisit {}

export interface IVisitExaminationRequest {
  weight_kg: number
  height_cm: number
  blood_pressure_systolic: number
  blood_pressure_diastolic: number
  pulse: number
  temperature_c?: number
  conclusion: string
  status?: string
  needs_ambulatory_exam: boolean
  referral_organization_id: string
}

export interface IVisitMedicalDeferralExamination {
  weight_kg: number
  height_cm: number
  blood_pressure_systolic: number
  blood_pressure_diastolic: number
  pulse: number
  conclusion: string
  needs_ambulatory_exam: boolean
  referral_organization_id: string
}

export interface IVisitMedicalDeferralRequest {
  /** The therapist's examination the deferral is based on. */
  examination: IVisitMedicalDeferralExamination | null
  deferral_end_date: string
  reason?: string
  organization_id: string
}

export const EExaminationStatus = {
  COMPLETED: 'COMPLETED',
  DEFERRED: 'DEFERRED',
} as const
export type TExaminationStatus =
  (typeof EExaminationStatus)[keyof typeof EExaminationStatus]

export interface IVisitExaminationHistoryItem {
  visit_id: string
  examined_at: string
  doctor: string
  conclusion: string
  status: TExaminationStatus
  status_label: string
}

export interface IVisitExaminationsResponse {
  success: boolean
  data: IVisitExaminationHistoryItem[]
}
export const ETemperatureRecommendation = {
  NORMAL: 'NORMAL',
  HIGH: 'HIGH',
  LOW: 'LOW',
} as const

export type TTemperatureRecommendation =
  (typeof ETemperatureRecommendation)[keyof typeof ETemperatureRecommendation]

export interface IVisitExaminationData {
  visit_id: string
  status: TVisitStatus
  bmi: number
  temperature_c?: number
  // Present only when the temperature is outside 36.0–37.4 °C. Advisory:
  // the visit is still saved, the therapist decides on a deferral manually.
  temperature_recommendation?: TTemperatureRecommendation
}

export interface IVisitExaminationResponse {
  success: boolean
  data: IVisitExaminationData
  errors: {
    code: string
    message: string
    fields: { key: string; value: string }[]
  }[]
  meta: {
    timestamp: string
    request_id: string
    execution_time_ms: number
  }
}

/**
 * Query params the admitted queue endpoint accepts: paging plus the
 * server-side filters (free-text `search` and an inclusive `date_from`/`date_to`
 * range, both `yyyy-MM-dd`).
 */
export interface IVisitsListParams extends IApiRequestPageable {
  /** ФИО, UID, birth date, or an exact 14-digit ПИНФЛ. */
  search?: string
  date_from?: string
  date_to?: string
  // ── Multi-select filters (BS-244): OR within one, AND across them ──────────
  status?: TAdmittedStatus[]
  donor_category_id?: string[]
  donation_type_id?: string[]
  gender?: TGender[]
  /** BLOOD_ADMIN only — everyone else sees their own station anyway. */
  organization_id?: string[]
}

/**
 * Where an admitted donor is now — the codes the `status` filter takes. The
 * row's `direction_badge` names the same stage, as localized text.
 */
export const EAdmittedStatus = {
  REGISTRY: 'REGISTRY',
  THERAPIST: 'THERAPIST',
  SCREENING: 'SCREENING',
  /** Забор — the test tubes only (BS-285); the donation queue is DONATION. */
  COLLECTION: 'COLLECTION',
  LAB: 'LAB',
  DONATION: 'DONATION',
  PROCESSING: 'PROCESSING',
  EXPEDITION: 'EXPEDITION',
  /** No active status left — the visit is finished. */
  FINISHED: 'FINISHED',
} as const
export type TAdmittedStatus =
  (typeof EAdmittedStatus)[keyof typeof EAdmittedStatus]

export type TGender = 'MALE' | 'FEMALE'
